import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import { Store } from "react-notifications-component";
import { Plus, Pencil, Trash2, GripVertical, Feather } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";
import { logoutUser } from "../../actions/authActions";
import { toggleDarkMode } from "../../actions/preferenceActions";
import AdminCommandPalette from "./AdminCommandPalette";
import AdminNotifications from "./AdminNotifications";

import {
  AdminAppShell,
  PageHeader,
  Button,
  IconButton,
  Tooltip,
  Spinner,
  EmptyState,
  SearchInput,
  Select,
  FormField,
  Badge,
  Breadcrumb,
} from "../../design-system";
import { buildAdminBreadcrumb } from "../../utils/adminBreadcrumb";

const TYPE_LABELS = {
  JOB:           "Emploi",
  PROJECT:       "Projet",
  CERTIFICATION: "Certification",
  EDUCATION:     "Formation",
  OTHER:         "Autre",
};

function SortableProRow({ professional, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: professional.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes} className="hover:bg-bg-2/50 transition-colors">
      <td className="w-10 px-3 py-3 align-middle">
        <button type="button" {...listeners} aria-label="Glisser pour réordonner"
          className="cursor-grab active:cursor-grabbing p-1 text-mute hover:text-text">
          <GripVertical className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </td>
      <td className="px-3 py-3 align-middle">
        <div className="font-semibold text-text leading-tight">{professional.title}</div>
      </td>
      <td className="px-3 py-3 align-middle"><Badge variant="neutral">{TYPE_LABELS[professional.type] || professional.type}</Badge></td>
      <td className="px-3 py-3 align-middle text-sm">{professional.company || "—"}</td>
      <td className="px-3 py-3 align-middle text-sm text-mute">{professional.location || "—"}</td>
      <td className="px-3 py-3 align-middle text-sm text-mute font-mono">{professional.period || "—"}</td>
      <td className="px-3 py-3 align-middle text-sm font-mono tabular-nums text-mute w-20 text-right">{professional.order}</td>
      <td className="px-3 py-3 align-middle w-32">
        <div className="flex items-center gap-0.5 justify-end">
          <Tooltip content="Modifier">
            <IconButton as={Link} to={`/dashboard/professionals/${professional.id}`}
              size="sm" variant="ghost" icon={<Pencil strokeWidth={1.8} />}
              aria-label={`Modifier ${professional.title}`} />
          </Tooltip>
          <Tooltip content="Supprimer">
            <IconButton size="sm" variant="ghost" icon={<Trash2 strokeWidth={1.8} />}
              aria-label={`Supprimer ${professional.title}`}
              onClick={() => onDelete(professional)} />
          </Tooltip>
        </div>
      </td>
    </tr>
  );
}

function SortableProTable({ professionals, onDragEnd, onDelete }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <div className="rounded-lg bg-cream overflow-hidden border border-ink/12 overflow-x-auto">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <table className="w-full text-sm border-collapse">
          <thead className="bg-ink text-cream">
            <tr>
              <th className="w-10 px-3 py-2.5"></th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Titre</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold w-32">Type</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Entreprise</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Localisation</th>
              <th className="text-left px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold">Période</th>
              <th className="text-right px-3 py-2.5 text-[10.5px] uppercase tracking-wider font-semibold w-20">Ordre</th>
              <th className="w-32 px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/8">
            <SortableContext items={professionals.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              {professionals.map((pro) => (
                <SortableProRow key={pro.id} professional={pro} onDelete={onDelete} />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
    </div>
  );
}

class ProfessionalList extends Component {
  constructor(props) {
    super(props);
    this.state = { professionals: [], loading: true, filterType: "" };
  }

  componentDidMount() {
    this.loadProfessionals();
  }

  loadProfessionals = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const params = new URLSearchParams();
      if (this.state.filterType) params.append("type", this.state.filterType);
      const response = await axios.get(`/api/professionals?${params.toString()}`);
      this.setState({ professionals: response.data.professionals || [], loading: false });
    } catch (error) {
      console.error("Error loading professionals:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: "Impossible de charger les professionnels",
        type: "danger",
        insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  handleFilterChange = (e) => {
    this.setState({ filterType: e.target.value }, this.loadProfessionals);
  };

  handleDelete = (professional) => {
    confirmAlert({
      title: "Supprimer ce professionnel ?",
      message: `« ${professional.title} » sera supprimé définitivement.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) setAuthToken(token);
          await axios.delete(`/api/professionals/${professional.id}`);
          Store.addNotification({
            title: "Professionnel supprimé",
            message: `« ${professional.title} » a été retiré.`,
            type: "success",
            insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
          this.loadProfessionals();
        } catch (error) {
          console.error("Error deleting professional:", error);
          Store.addNotification({
            title: "Erreur",
            message: "Impossible de supprimer",
            type: "danger",
            insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
        }
      },
    });
  };

  handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = this.state.professionals.findIndex((p) => p.id === active.id);
    const newIndex = this.state.professionals.findIndex((p) => p.id === over.id);
    const newPros = arrayMove(this.state.professionals, oldIndex, newIndex);
    this.setState({ professionals: newPros });

    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const orderList = newPros.map((p, i) => ({ id: p.id, order: i }));
      await axios.put("/api/professionals/reorder", { orderList });
      this.setState({ professionals: newPros.map((p, i) => ({ ...p, order: i })) });
      Store.addNotification({
        title: "Ordre mis à jour",
        type: "success",
        insert: "top", container: "top-right", dismiss: { duration: 2000 },
      });
    } catch (error) {
      console.error("Error reordering:", error);
      this.loadProfessionals();
      Store.addNotification({
        title: "Erreur",
        message: "Impossible de mettre à jour l'ordre",
        type: "danger",
        insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  onLogout = () => {
    confirmAlert({
      title: "Déconnexion",
      message: "Êtes-vous sûr de vouloir vous déconnecter ?",
      type: "warning",
      confirmText: "Déconnexion",
      cancelText: "Annuler",
      onConfirm: () => {
        this.props.logoutUser();
        this.props.history.push("/login");
      },
    });
  };

  onToggleDarkMode = () => {
    const { _preferences } = this.props;
    this.props.toggleDarkMode(!_preferences?._dark);
  };

  render() {
    const { professionals, loading, filterType } = this.state;
    const { user, _preferences, location } = this.props;

    return (
      <AdminAppShell
        user={user}
        currentPath={location.pathname}
        isDarkMode={_preferences?._dark || false}
        onToggleDarkMode={this.onToggleDarkMode}
        onLogout={this.onLogout}
        LinkComponent={Link}
        pageTitle="Professionnels"
        breadcrumb={<Breadcrumb items={buildAdminBreadcrumb(location.pathname)} LinkComponent={Link} />}
        topbarCenter={<AdminCommandPalette />}
        topbarRight={<AdminNotifications />}
      >
        <div className="w-full px-5 sm:px-6 lg:px-8 xl:px-10 py-6 lg:py-8 space-y-6 lg:space-y-7">
          <PageHeader
            size="hero"
            eyebrow="— Section iv · Professionnels"
            title={<>Emplois, projets, <span className="font-serif italic font-normal align-baseline">certifs.</span></>}
            description="Toutes les entrées qui composent ton parcours pro, formation et certifications confondues. L'ordre se règle au drag-and-drop."
            actions={
              <Button as={Link} to="/dashboard/professionals/new" leftIcon={<Feather className="w-4 h-4" strokeWidth={1.8} />}>
                Nouvelle entrée
              </Button>
            }
          />

          <div className="flex items-end gap-3 mb-4">
            <FormField label="Type" className="min-w-[240px]">
              <Select
                value={filterType}
                onChange={this.handleFilterChange}
                placeholder="Tous les types"
                options={Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label }))}
              />
            </FormField>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
          ) : professionals.length === 0 ? (
            <EmptyState
              title="Aucun professionnel"
              description="Ajoute ta première entrée : emploi, projet, certif ou formation."
              action={<Button as={Link} to="/dashboard/professionals/new" leftIcon={<Plus className="w-4 h-4" />}>Nouveau</Button>}
            />
          ) : (
            <SortableProTable professionals={professionals} onDragEnd={this.handleDragEnd} onDelete={this.handleDelete} />
          )}
        </div>
      </AdminAppShell>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
  _preferences: state._preferences,
});

export default connect(mapStateToProps, { logoutUser, toggleDarkMode })(withRouter(ProfessionalList));
